import { useEffect, useRef } from "react";

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Se llama al completar el código (para enviar sin tener que pulsar el botón). */
  onComplete?: (value: string) => void;
  /** `digits`: 6 dígitos. `recovery`: código de respaldo (letras y números). */
  variant?: "digits" | "recovery";
  disabled?: boolean;
  autoFocus?: boolean;
  isDarkMode: boolean;
  ariaLabel: string;
}

const DIGITS = 6;

/**
 * Campo para un código de verificación. Un solo `<input>` y no seis cajitas: así
 * funcionan pegar, el autocompletado del SMS (`one-time-code`) y los lectores de
 * pantalla sin trucos.
 */
export default function CodeInput({
  value,
  onChange,
  onComplete,
  variant = "digits",
  disabled = false,
  autoFocus = false,
  isDarkMode,
  ariaLabel,
}: CodeInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus, variant]);

  const clean = (raw: string) =>
    variant === "digits"
      ? raw.replace(/\D/g, "").slice(0, DIGITS)
      : raw.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 11);

  const complete = (text: string) =>
    variant === "digits" ? text.length === DIGITS : text.replace(/-/g, "").length === 10;

  return (
    <input
      ref={ref}
      value={value}
      onChange={(event) => {
        const next = clean(event.target.value);
        onChange(next);
        if (complete(next) && !complete(value)) onComplete?.(next);
      }}
      disabled={disabled}
      inputMode={variant === "digits" ? "numeric" : "text"}
      autoComplete="one-time-code"
      autoCapitalize="characters"
      spellCheck={false}
      aria-label={ariaLabel}
      placeholder={variant === "digits" ? "000000" : "XXXXX-XXXXX"}
      className={`w-full rounded-lg border px-3 py-3 text-center font-mono text-2xl tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${
        isDarkMode
          ? "border-dark-border bg-dark-surface text-white placeholder:text-gray-600"
          : "border-gray-300 bg-white text-gray-900 placeholder:text-gray-300"
      } ${variant === "recovery" ? "text-lg tracking-[0.2em]" : ""}`}
    />
  );
}
